import React, { useMemo } from 'react';
import type {
  PhieuXuatDieuChuyenCellValue,
  PhieuXuatDieuChuyenChiTiet,
  PhieuXuatDieuChuyenPasteConfigs,
  PhieuXuatDieuChuyenVatTuData
} from '@/types/schemas';
import type { DataTableRenderCellParams, DataTableRowEventHandler, RepeatRowsPayload } from '@/components/custom/erp';
import { useAutoSelectBoPhan } from './hooks/useAutoSelectBoPhan';
import { SelectedCellInfo, useDetailPaste } from '../hooks';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

interface DetailTabProps {
  formMode: FormMode;
  rows: PhieuXuatDieuChuyenChiTiet[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<PhieuXuatDieuChuyenChiTiet>;
  onCellClick?: (params: DataTableRenderCellParams<PhieuXuatDieuChuyenChiTiet>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: PhieuXuatDieuChuyenCellValue) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<PhieuXuatDieuChuyenChiTiet>) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (configs: PhieuXuatDieuChuyenPasteConfigs) => void;
}

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs
}) => {
  const { fetchDetail } = useCRUD<PhieuXuatDieuChuyenVatTuData>({
    endpoint: QUERY_KEYS.VAT_TU,
    initialFetch: false
  });
  const handleCellValueChange = useAutoSelectBoPhan(onCellValueChange);
  const columns = useMemo(
    () => getDetailTableColumns(handleCellValueChange, fetchDetail, formMode),
    [fetchDetail, handleCellValueChange, formMode]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <div className='h-[500px] w-full'>
      <ErpInputTableV2
        rows={rows}
        autoHeight
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={row => row?.uuid || ''}
        enableContextMenu={true}
        onCopyData={({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => {
              console.log('Copy data per column:', text);
            }
          });
        }}
        onRepeatForRows={onRepeatForRows}
        pasteTarget={selectedCell}
        actions={{
          add: onAddRow,
          delete: onDeleteRow,
          copy: onCopyRow,
          paste: onPasteRow,
          moveUp: () => onMoveRow('up'),
          moveDown: () => onMoveRow('down'),
          export: () => console.log('Export clicked'),
          pin: () => console.log('Pin clicked')
        }}
        actionsDisabled={formMode === 'view'}
      />
    </div>
  );
};
